import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import {
	SolutionCenterContentTypes,
	ZendeskArticle,
	ZendeskCategory,
	ZendeskSection,
} from '../../../types/solution-center';
import { GetZendeskArticlesBySectionId } from '../../../connection/zendesk';
import {
	LoadingContainer,
	SectionContainer,
	SectionExpandText,
	SectionHeader,
	SectionList,
	SectionListItem,
	Shadow,
} from '../elements';
import { setSolutionCenterContentState } from '../../../stores/actions/user-interface';
import LoadingSpinner, { LoadingSpinnerType } from '../../general-components/loading-spinner';

const ARTICLE_LIMT = 6;

interface CategoryViewProps {
	currentCategory: ZendeskCategory;
	sections: ZendeskSection[];
	handleSelectSection: (sectionId: number) => void;
}

interface ArticleMapValue {
	articles: ZendeskArticle[];
	totalArticleCount: number;
}

export default function CategoryView(props: CategoryViewProps) {
	const dispatch = useDispatch();

	const {
		currentCategory,
		sections,
		handleSelectSection,
	} = props;

	const [secArticleMap, setSecArticleMap] = useState<Map<number, ArticleMapValue>>(new Map());
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const currentCategorySections = useMemo(
		() => sections.filter((section) => section.category_id === currentCategory.id),
		[sections, currentCategory],
	);

	const getSectionArticles = async () => {
		setIsLoading(true);
		const newSectionArticleMap = new Map<number, ArticleMapValue>();
		await Promise.all(
			currentCategorySections.map(
				(section) => GetZendeskArticlesBySectionId(section.id, ARTICLE_LIMT).then((res) => {
					if (res.articles) {
						const articleMapValue: ArticleMapValue = {
							articles: res.articles,
							totalArticleCount: res.count,
						};
						newSectionArticleMap.set(section.id, articleMapValue);
					}
				}),
			),
		);
		setSecArticleMap(newSectionArticleMap);
		setIsLoading(false);
	};

	const renderSectionArticles = (sectionId: number) => {
		const sectionArticles = secArticleMap.get(sectionId);
		if (sectionArticles) {
			const showSectionLink = sectionArticles.totalArticleCount > ARTICLE_LIMT;
			return (
				<>
					<SectionList className={classNames(showSectionLink && 'show-section-link')}>
						{sectionArticles.articles.map((article) => (
							<SectionListItem
								key={`article-${article.id}`}
								onClick={() => dispatch(setSolutionCenterContentState({
									id: article.id,
									type: SolutionCenterContentTypes.Article,
								}))}
							>
								{article.title}
							</SectionListItem>
						))}
					</SectionList>
					{showSectionLink && (
						<SectionExpandText onClick={() => handleSelectSection(sectionId)}>
							{`View all ${sectionArticles.totalArticleCount} articles`}
						</SectionExpandText>
					)}
				</>
			);
		}
		return <></>;
	};

	useEffect(() => {
		getSectionArticles();
	}, [currentCategorySections]);

	return (
		isLoading ? (
			<LoadingContainer>
				<LoadingSpinner
					height={36}
					width={36}
					type={LoadingSpinnerType.Gradient}
					style={{ background: '#26242B' }}
				/>
			</LoadingContainer>
		) : (
			<>
				<div>
					{currentCategorySections.map((section) => (
						<SectionContainer key={`section-${section.id}`}>
							<SectionHeader
								className="can-click"
								onClick={() => handleSelectSection(section.id)}
							>
								{section.name}
							</SectionHeader>
							<SectionHeader>&nbsp;&gt;</SectionHeader>
							{renderSectionArticles(section.id)}
						</SectionContainer>
					))}
				</div>
				<Shadow />
			</>
		)
	);
}
